import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Star, Bookmark, X, MessageSquare, IdCard,
  TrendingUp,
  Briefcase, Loader2, Send, Info, AlertCircle,
  Heart, MapPin, Globe, Building2,
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useUserProfile } from '../contexts/UserProfileContext';
import {
  RecruiterInlineEmpty,
  RecruiterLoadingScreen,
  RecruiterPageShell,
} from '../components/Recruiter/RecruiterStates';
import RecruiterLoggedOutGate from '../components/RecruiterLoggedOutGate';
import AtsPushButton from '../components/AtsPushButton';
import PassportViewer from '../components/Passport/PassportViewer';

const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

interface SpotlightProfile {
  base_locations?: string;
  preferred_locations?: string;
  role_preferences?: string;
  current_role?: string;
  current_level?: string;
  career_status?: string;
  cv_language?: string;
}

interface Spotlight {
  id: string;
  company_id: string;
  cv_id: string;
  candidate_user_id: string;
  target_role: string | null;
  summary: string;
  fit: string;
  status: string;
  position: number;
  created_at: number;
  seen_at: number | null;
  profile: SpotlightProfile;
}

const JOB_SAVES_KEY = 'cevee_hb_job_saves';

const loadJobSaves = (): Record<string, string[]> => {
  try { return JSON.parse(localStorage.getItem(JOB_SAVES_KEY) || '{}'); } catch { return {}; }
};

const addJobSave = (cvId: string, jobTitle: string) => {
  const saves = loadJobSaves();
  const jobs = saves[cvId] || [];
  if (!jobs.includes(jobTitle)) saves[cvId] = [...jobs, jobTitle];
  try { localStorage.setItem(JOB_SAVES_KEY, JSON.stringify(saves)); } catch {}
};

const removeJobSave = (cvId: string, jobTitle: string) => {
  const saves = loadJobSaves();
  const jobs = (saves[cvId] || []).filter((j) => j !== jobTitle);
  if (jobs.length) saves[cvId] = jobs; else delete saves[cvId];
  try { localStorage.setItem(JOB_SAVES_KEY, JSON.stringify(saves)); } catch {}
};

const getJobSaves = (cvId: string): string[] => {
  return loadJobSaves()[cvId] || [];
};

const Spotlights: React.FC = () => {
  const { t, i18n } = useTranslation('spotlights');
  const { t: tCard } = useTranslation('hiringBoard');
  const navigate = useNavigate();
  const { apiCall, isSignedIn, user, authReady, isLoading: authLoading } = useAuth();
  const { userProfile } = useUserProfile();

  const [spotlights, setSpotlights] = useState<Spotlight[]>([]);
  const [newCount, setNewCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [noCompany, setNoCompany] = useState(false);

  // Recruiter profile completeness check
  const [recruiterProfile, setRecruiterProfile] = useState<any>(null);
  useEffect(() => {
    if (!user?.userId || !authReady) return;
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/profiles/${user.userId}`);
        if (resp.ok) setRecruiterProfile(await resp.json());
      } catch {}
    })();
  }, [user?.userId, authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  const isProfileIncomplete = recruiterProfile && recruiterProfile.is_recruiter && (
    !recruiterProfile.first_name || !recruiterProfile.last_name || !recruiterProfile.company_name ||
    !recruiterProfile.recruiter_background ||
    !recruiterProfile.consents?.recruiter_profile_visibility?.granted
  );

  // Passport modal state (recruiter view of a candidate's full passport)
  const [cvModalSpotlight, setCvModalSpotlight] = useState<Spotlight | null>(null);

  // Profile nudge state
  const [showProfileNudge, setShowProfileNudge] = useState(false);

  // Reach-out state
  const [showReachOut, setShowReachOut] = useState(false);
  const [reachOutSpotlight, setReachOutSpotlight] = useState<Spotlight | null>(null);
  const [reachOutForm, setReachOutForm] = useState({ job_title: '', salary_range: '', message: '', nice_to_know: '' });
  const [reachOutSubmitting, setReachOutSubmitting] = useState(false);
  const [reachOutSuggestions, setReachOutSuggestions] = useState<any>(null);
  const introTemplateRef = useRef('');
  const isTemplateMessageRef = useRef(false);

  // Info sheet
  const [showInfoTip, setShowInfoTip] = useState(false);

  // Dismiss confirmation
  const [dismissTarget, setDismissTarget] = useState<Spotlight | null>(null);

  // Fit modal
  const [fitSpotlight, setFitSpotlight] = useState<Spotlight | null>(null);

  // Toast
  const [toast, setToast] = useState<{ message: string; type: string } | null>(null);
  const showToast = (message: string, type: string = 'info', duration = 3000) => {
    setToast({ message, type });
    setTimeout(() => setToast(null), duration);
  };

  // Resolve company_id via my-company endpoint
  useEffect(() => {
    if (!user || !authReady) return;
    (async () => {
      try {
        const resp = await apiCall(`${API_BASE}/api/v1/companies/my-company`);
        if (resp.ok) {
          const data = await resp.json();
          const company = data.company;
          if (company && company.id) {
            setCompanyId(company.id);
          } else {
            setNoCompany(true);
          }
        } else {
          setNoCompany(true);
        }
      } catch {
        setNoCompany(true);
      }
    })();
  }, [user?.userId, authReady]); // eslint-disable-line react-hooks/exhaustive-deps

  // ⚠️ IMPORTANT: reachOutMap is keyed by candidate_user_id, NOT cv_id.
  // cv_ids change when candidates update their CV - candidate_user_id is stable.
  const [reachOutMap, setReachOutMap] = useState<Record<string, any>>({});

  // Global saved-cvs set (source of truth for bookmark state)
  const [savedCvIds, setSavedCvIds] = useState<Set<string>>(new Set());

  // Load spotlights + check existing reach-outs
  const loadSpotlights = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/company/${companyId}`);
      if (resp.ok) {
        const data = await resp.json();
        const items = data.spotlights || [];
        setSpotlights(items);
        setNewCount(data.new_count || 0);

        // ⚠️ Use candidate_user_id (stable), NOT cv_id - cv_ids change on CV update
        const candidateIds = items.map((s: Spotlight) => s.candidate_user_id).filter(Boolean);
        if (candidateIds.length > 0) {
          try {
            const [roResp, profileResp] = await Promise.allSettled([
              apiCall(`${API_BASE}/api/v1/companies/reach-out-status-by-candidate-ids`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ candidate_ids: candidateIds }),
              }),
              apiCall(`${API_BASE}/api/v1/profiles/batch`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ user_ids: candidateIds }),
              }),
            ]);
            if (roResp.status === 'fulfilled' && roResp.value.ok) {
              const roData = await roResp.value.json();
              setReachOutMap(roData.status_map || {});
            }
            if (profileResp.status === 'fulfilled' && profileResp.value.ok) {
              const pData = await profileResp.value.json();
              const cvMap: Record<string, string> = {};
              for (const p of pData.profiles || []) {
                if (p.id && p.cv_id) cvMap[p.id] = p.cv_id;
              }
              setSpotlights((prev) =>
                prev.map((s) => {
                  const freshCvId = cvMap[s.candidate_user_id];
                  return freshCvId && freshCvId !== s.cv_id ? { ...s, cv_id: freshCvId } : s;
                })
              );
            }
          } catch {}
        }

        // Fetch global saved-cvs to sync bookmark state
        try {
          const savedResp = await apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`);
          if (savedResp.ok) {
            const savedData = await savedResp.json();
            const ids = (savedData.saved_cvs || []).map((c: any) => c.cv_id);
            setSavedCvIds(new Set(ids));
          }
        } catch {}

        if (data.new_count > 0) {
          await apiCall(`${API_BASE}/api/v1/spotlights/company/${companyId}/mark-seen`, {
            method: 'POST',
          });
        }
      }
    } catch (err) {
      console.error('Failed to load spotlights:', err);
    } finally {
      setLoading(false);
    }
  }, [companyId, apiCall]);

  useEffect(() => { loadSpotlights(); }, [loadSpotlights]);

  // Re-check reach-out + saved status when page regains focus
  useEffect(() => {
    const refreshOnFocus = async () => {
      if (!companyId || !spotlights.length) return;
      const candidateIds = spotlights.map(s => s.candidate_user_id).filter(Boolean);
      try {
        const [roResp, savedResp] = await Promise.all([
          candidateIds.length > 0
            ? apiCall(`${API_BASE}/api/v1/companies/reach-out-status-by-candidate-ids`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ candidate_ids: candidateIds }),
              })
            : Promise.resolve(null),
          apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`),
        ]);
        if (roResp?.ok) {
          const roData = await roResp.json();
          setReachOutMap(roData.status_map || {});
        }
        if (savedResp.ok) {
          const savedData = await savedResp.json();
          const ids = (savedData.saved_cvs || []).map((c: any) => c.cv_id);
          setSavedCvIds(new Set(ids));
        }
      } catch {}
    };
    const onFocus = () => { refreshOnFocus(); };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [companyId, spotlights, apiCall]);

  // Status update - saved stays visible, dismissed/reached_out disappear
  const updateStatus = async (spotlightId: string, status: string) => {
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/spotlights/${spotlightId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (resp.ok) {
        if (status === 'saved' || status === 'seen') {
          setSpotlights(prev => prev.map(s => s.id === spotlightId ? { ...s, status } : s));
        } else {
          setSpotlights(prev => prev.filter(s => s.id !== spotlightId));
        }
      }
    } catch (err) {
      console.error('Failed to update spotlight status:', err);
    }
  };

  // Toggle bookmark: save ↔ unsave (syncs with global saved-cvs)
  const toggleSave = async (spotlight: Spotlight) => {
    const jobTitle = spotlight.target_role || 'Spotlight';
    const jobSaves = getJobSaves(spotlight.cv_id);
    const isSavedForJob = jobSaves.includes(jobTitle);
    const wasGloballySaved = savedCvIds.has(spotlight.cv_id);

    if (isSavedForJob) {
      removeJobSave(spotlight.cv_id, jobTitle);
      const remaining = getJobSaves(spotlight.cv_id);
      if (remaining.length === 0 && wasGloballySaved) {
        setSavedCvIds(prev => { const next = new Set(prev); next.delete(spotlight.cv_id); return next; });
        updateStatus(spotlight.id, 'seen');
        apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs/${spotlight.cv_id}`, { method: 'DELETE' }).catch(() => {});
      }
    } else {
      addJobSave(spotlight.cv_id, jobTitle);
      if (!wasGloballySaved) {
        setSavedCvIds(prev => new Set(prev).add(spotlight.cv_id));
        updateStatus(spotlight.id, 'saved');
        showToast(t('actions.saved_success'), 'success');
        apiCall(`${API_BASE}/api/v1/profiles/me/saved-cvs`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ cv_id: spotlight.cv_id, prompt: (jobTitle).slice(0, 100) }),
        }).catch(() => {});
      } else {
        showToast(t('actions.saved_success'), 'success');
      }
    }
  };

  // Open the recruiter passport modal. PassportViewer fetches its own CV
  // images / passport JSON; we only pass the (already freshened) cv_id and the
  // recruiter-gated prefs from the spotlight feed.
  const openCvModal = (spotlight: Spotlight) => {
    setCvModalSpotlight(spotlight);
  };

  const closeCvModal = () => {
    setCvModalSpotlight(null);
  };

  // Reach-out
  const openReachOut = async (spotlight: Spotlight) => {
    if (isProfileIncomplete) { setShowProfileNudge(true); return; }
    setReachOutSpotlight(spotlight);
    setShowReachOut(true);

    const prefillTitle = spotlight.target_role || '';
    setReachOutForm((prev) => ({ ...prev, job_title: prefillTitle }));

    try {
      const r = await apiCall(`${API_BASE}/api/v1/companies/my/reach-out-suggestions`);
      if (r.ok) {
        const data = await r.json();
        if (data.reach_out_suggestions) {
          const s = data.reach_out_suggestions;
          if (Array.isArray(s.job_titles)) {
            const seen = new Set<string>();
            s.job_titles = s.job_titles.filter((jt: any) => {
              const key = jt.title?.toLowerCase();
              if (!key || seen.has(key)) return false;
              seen.add(key);
              return true;
            });
          }
          setReachOutSuggestions(s);

          if (prefillTitle && s.intro) {
            const lang = i18n.language?.startsWith('de') ? 'de' : 'en';
            const rawIntro = s.intro[lang] || s.intro.en || s.intro.de || '';
            if (rawIntro) {
              const intro = rawIntro.replace(/\{\{job_title\}\}/g, prefillTitle);
              introTemplateRef.current = rawIntro;
              isTemplateMessageRef.current = true;
              setReachOutForm((prev) => ({ ...prev, message: intro }));
            }
          }
        }
      }
    } catch {}
  };

  const submitReachOut = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!reachOutSpotlight) return;
    if (isProfileIncomplete) { setShowProfileNudge(true); return; }
    if (!reachOutForm.job_title.trim()) { showToast(t('reach_out.job_title_required'), 'error'); return; }
    if (!reachOutForm.salary_range.trim()) { showToast(t('reach_out.salary_required'), 'error'); return; }
    if (!reachOutForm.message.trim()) { showToast(t('reach_out.message_required'), 'error'); return; }
    setReachOutSubmitting(true);
    try {
      const resp = await apiCall(`${API_BASE}/api/v1/recruiter/reach-out`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          candidate_user_id: reachOutSpotlight.candidate_user_id,
          cv_id: reachOutSpotlight.cv_id,
          job_title: reachOutForm.job_title,
          message: reachOutForm.message,
          salary_range: reachOutForm.salary_range,
          nice_to_know: reachOutForm.nice_to_know,
        }),
      });
      if (resp.ok) {
        const data = await resp.json().catch(() => null);
        showToast(t('reach_out.sent'), 'success');
        setShowReachOut(false);
        setReachOutForm({ job_title: '', salary_range: '', message: '', nice_to_know: '' });
        if (data?.conversation_id) {
          setReachOutMap(prev => ({ ...prev, [reachOutSpotlight.candidate_user_id]: { status: 'candidate_sparked', conversation_id: data.conversation_id, reach_out_id: data.vector_id } }));
        }
        updateStatus(reachOutSpotlight.id, 'reached_out');
      } else if (resp.status === 429) {
        showToast(t('reach_out.rate_limit'), 'error', 6000);
      } else {
        const data = await resp.json().catch(() => null);
        showToast(data?.detail || t('reach_out.failed'), 'error');
      }
    } catch (err: any) {
      showToast(err.message || t('reach_out.failed'), 'error');
    } finally {
      setReachOutSubmitting(false);
    }
  };

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      new: 'border border-pink-100 bg-pink-50 text-pink-600',
      seen: 'bg-slate-100 text-slate-500',
      saved: 'border border-slate-300 bg-white text-slate-700',
    };
    const labels: Record<string, string> = {
      new: t('badge.new'),
      seen: t('badge.seen'),
      saved: t('badge.saved'),
    };
    return (
      <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${styles[status] || styles.seen}`}>
        {labels[status] || status}
      </span>
    );
  };

  if (authLoading) {
    return <RecruiterLoadingScreen icon={Star} />;
  }

  if (!isSignedIn || !userProfile?.is_recruiter) {
    return <RecruiterLoggedOutGate returnTo="/spotlights" showLogin={!isSignedIn} />;
  }

  if (noCompany) {
    return (
      <RecruiterPageShell>
        <RecruiterInlineEmpty
          icon={Building2}
          title={t('no_company.title')}
          description={t('no_company.description')}
          action={
            <Link
              to="/company/manage"
              className="inline-flex items-center gap-2 rounded-xl bg-slate-900 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-slate-800"
            >
              <Building2 className="size-4" /> {t('no_company.cta')}
            </Link>
          }
        />
      </RecruiterPageShell>
    );
  }

  if (loading) {
    return <RecruiterLoadingScreen icon={Star} />;
  }

  return (
    <RecruiterPageShell>
      {spotlights.length === 0 ? (
        <RecruiterInlineEmpty
          icon={Star}
          title={t('empty_title')}
          description={t('empty_text')}
        />
      ) : (
        <>
          {/* Toolbar */}
          <div className="mb-5 rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex min-w-0 items-center gap-2">
                <p className="text-sm text-slate-600">{t('subtitle')}</p>
                <button
                  type="button"
                  onClick={() => setShowInfoTip(true)}
                  className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
                  aria-label={t('info_sheet.ai_title')}
                >
                  <Info className="size-3.5" />
                </button>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm">
                <span className="text-slate-500">
                  <span className="font-semibold tabular-nums text-slate-900">{spotlights.length}</span>
                  {' '}{t('stats.spotlights')}
                </span>
                {newCount > 0 && (
                  <span className="rounded-full border border-pink-100 bg-pink-50 px-2.5 py-0.5 text-xs font-medium text-pink-600">
                    {newCount} {t('stats.new')}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* Candidate grid */}
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {spotlights.map((s) => {
              const isSaved = getJobSaves(s.cv_id).includes(s.target_role || 'Spotlight');
              const cardStatus = isSaved ? 'saved' : (s.status === 'saved' ? 'seen' : s.status);
              const contacted = !!reachOutMap[s.candidate_user_id];

              return (
                <article
                  key={s.id}
                  className="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-colors hover:border-slate-300"
                >
                  <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-2.5">
                    <div className="flex min-w-0 items-center gap-2">
                      {s.target_role ? (
                        <span className="truncate text-xs font-semibold text-slate-700">{s.target_role}</span>
                      ) : (
                        <span className="truncate text-xs font-medium uppercase tracking-wide text-slate-400">{t('title')}</span>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {statusBadge(cardStatus)}
                      {contacted && (
                        <span className="rounded-full border border-emerald-100 bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-600">
                          {t('badge.already_contacted')}
                        </span>
                      )}
                      {s.profile.cv_language && (
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-semibold uppercase text-slate-600">
                          {s.profile.cv_language}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleSave(s)}
                        className={`rounded-lg p-1 transition-colors ${
                          isSaved ? 'text-slate-900' : 'text-slate-300 hover:text-slate-500'
                        }`}
                        aria-label={t('actions.save')}
                      >
                        <Bookmark className={`size-3.5 ${isSaved ? 'fill-current' : ''}`} />
                      </button>
                      <button
                        type="button"
                        onClick={() => setDismissTarget(s)}
                        className="rounded-lg p-1 text-slate-300 transition-colors hover:bg-red-50 hover:text-red-500"
                        aria-label={t('actions.dismiss')}
                      >
                        <X className="size-3.5" />
                      </button>
                    </div>
                  </div>

                  <div className="flex flex-1 flex-col p-4">
                    <div className="min-w-0 flex-1">
                      {s.profile.current_role && (
                        <h3 className="text-sm font-semibold leading-snug text-slate-900">{s.profile.current_role}</h3>
                      )}
                      {s.profile.current_level && (
                        <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-xs font-medium text-slate-500">
                          {s.profile.current_level}
                        </span>
                      )}

                      <div className="mt-2 space-y-1 text-xs leading-snug text-slate-600">
                        {s.profile.base_locations && (
                          <p className="flex min-w-0 items-start gap-1.5">
                            <MapPin className="mt-0.5 size-3 shrink-0 text-slate-400" aria-hidden />
                            <span className="min-w-0">
                              <span className="font-medium text-slate-400">{tCard('card.base_location')}</span>
                              {' · '}
                              {s.profile.base_locations}
                            </span>
                          </p>
                        )}
                        {s.profile.role_preferences && (
                          <p className="flex min-w-0 items-start gap-1.5">
                            <Heart className="mt-0.5 size-3 shrink-0 fill-pink-400 text-pink-400" aria-hidden />
                            <span className="min-w-0 line-clamp-2">
                              <span className="font-medium text-slate-400">{tCard('card.seeking')}</span>
                              {' · '}
                              {s.profile.role_preferences}
                            </span>
                          </p>
                        )}
                        {s.profile.preferred_locations && (
                          <p className="flex min-w-0 items-start gap-1.5">
                            <Globe className="mt-0.5 size-3 shrink-0 text-slate-400" aria-hidden />
                            <span className="min-w-0">
                              <span className="font-medium text-slate-400">{tCard('card.preferred_location')}</span>
                              {' · '}
                              {s.profile.preferred_locations}
                            </span>
                          </p>
                        )}
                      </div>

                      <ul className="mt-3 space-y-1">
                        {(s.summary.includes('\n') ? s.summary.split('\n').filter(Boolean) : [s.summary]).map((b, i) => (
                          <li key={i} className="flex items-start gap-2 text-xs leading-relaxed text-slate-600">
                            <span className="mt-1.5 size-1 shrink-0 rounded-full bg-pink-300" aria-hidden />
                            <span className="line-clamp-2">{b}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  <div className="mt-auto flex flex-wrap items-center gap-0.5 border-t border-slate-100 px-3 py-2">
                    <button
                      type="button"
                      onClick={() => setFitSpotlight(s)}
                      className="flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition-all hover:bg-slate-100 hover:text-slate-900"
                    >
                      <TrendingUp className="size-3.5" />
                      Fit
                    </button>
                    <button
                      type="button"
                      onClick={() => openCvModal(s)}
                      className="flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-slate-600 transition-all hover:bg-slate-100 hover:text-slate-900"
                    >
                      <IdCard className="size-3.5" />
                      {t('actions.view_passport', 'Passport')}
                    </button>
                    {reachOutMap[s.candidate_user_id]?.conversation_id ? (
                      <button
                        type="button"
                        onClick={() => navigate(`/recruiter-inbox?conversation=${reachOutMap[s.candidate_user_id].conversation_id}`)}
                        className="flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-emerald-600 transition-all hover:bg-emerald-50"
                      >
                        <MessageSquare className="size-3.5" />
                        {t('actions.go_to_chat')}
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => openReachOut(s)}
                        className="flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-[11px] font-semibold text-pink-600 transition-all hover:bg-pink-50"
                      >
                        <Send className="size-3.5" />
                        {t('actions.contact')}
                      </button>
                    )}
                    {companyId && (
                      <AtsPushButton
                        companyId={companyId}
                        candidateUserId={s.candidate_user_id}
                        cvId={s.cv_id}
                        t={t as any}
                        appearance="outline"
                        hideIcons
                      />
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}

      {/* Passport Modal (recruiter view: CV · Stations · Map · Skills · Status · Pre-Screen) */}
      <AnimatePresence>
        {cvModalSpotlight && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[10050] flex items-center justify-center bg-black/50 p-2 sm:p-4"
            onClick={closeCvModal}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-[600px] w-full h-[90vh] overflow-hidden flex flex-col"
            >
              {/* Compact header */}
              <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                <span className="flex items-center gap-1.5 text-xs font-semibold text-slate-500">
                  <IdCard className="w-3.5 h-3.5" /> {t('actions.view_passport', 'Passport')}
                </span>
                <button onClick={closeCvModal} className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 transition-colors">
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="flex-1 min-h-0">
                <PassportViewer
                  cvId={cvModalSpotlight.cv_id}
                  audience="recruiter"
                  recruiterPrefs={cvModalSpotlight.profile}
                  candidateId={cvModalSpotlight.candidate_user_id}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reach-Out Modal */}
      <AnimatePresence>
        {showReachOut && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
            onClick={() => setShowReachOut(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="max-h-[90vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:max-w-lg sm:rounded-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-center pt-2 pb-0 sm:hidden">
                <div className="h-1 w-10 rounded-full bg-slate-300" />
              </div>

              <div className="flex items-center justify-between border-b border-slate-200 px-4 py-4 sm:px-5">
                <div className="flex items-center gap-2.5">
                  <div className="flex size-8 items-center justify-center rounded-lg bg-slate-900">
                    <MessageSquare className="size-4 text-white" />
                  </div>
                  <h2 className="text-base font-semibold text-slate-900">{t('reach_out.title')}</h2>
                </div>
                <button type="button" onClick={() => setShowReachOut(false)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                  <X className="size-5" />
                </button>
              </div>

              <form onSubmit={submitReachOut} className="space-y-4 px-4 py-4 sm:px-5">
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">{t('reach_out.job_title')} <span className="text-red-500">*</span></label>
                  {reachOutSuggestions?.job_titles && reachOutSuggestions.job_titles.length > 0 && (
                    <div className="mb-2 flex flex-wrap gap-1.5">
                      {reachOutSuggestions.job_titles.map((jt: any, idx: number) => {
                        const label = jt.location ? `${jt.title} (${jt.location})` : jt.title;
                        const isSelected = reachOutForm.job_title === label;
                        return (
                          <button
                            key={idx}
                            type="button"
                            onClick={() => {
                              setReachOutForm((prev) => {
                                const newTitle = prev.job_title === label ? '' : label;
                                const message = isTemplateMessageRef.current
                                  ? introTemplateRef.current.replace(/\{\{job_title\}\}/g, newTitle || '{{job_title}}')
                                  : prev.message;
                                return { ...prev, job_title: newTitle, message };
                              });
                            }}
                            className={`rounded-full border px-2.5 py-1 text-xs transition-colors ${
                              isSelected
                                ? 'border-slate-900 bg-slate-900 text-white'
                                : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                            }`}
                          >
                            <span className="flex items-center gap-1">
                              <Briefcase className="size-3 shrink-0" />
                              {jt.title}
                              {jt.location && <span className="text-[10px] opacity-70">· {jt.location}</span>}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  )}
                  <input
                    value={reachOutForm.job_title}
                    onChange={(e) => {
                      const val = e.target.value;
                      setReachOutForm((prev) => {
                        const message = isTemplateMessageRef.current
                          ? introTemplateRef.current.replace(/\{\{job_title\}\}/g, val || '{{job_title}}')
                          : prev.message;
                        return { ...prev, job_title: val, message };
                      });
                    }}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10"
                    placeholder={t('reach_out.job_title_placeholder')}
                    maxLength={300}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">{t('reach_out.salary_range')} <span className="text-red-500">*</span></label>
                  <input
                    value={reachOutForm.salary_range}
                    onChange={(e) => setReachOutForm((f) => ({ ...f, salary_range: e.target.value }))}
                    className="w-full rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10"
                    placeholder={t('reach_out.salary_placeholder')}
                    maxLength={100}
                    required
                  />
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">
                    {t('reach_out.message')} <span className="text-red-500">*</span>
                    {reachOutSuggestions?.intro && !reachOutForm.message && (
                      <button
                        type="button"
                        onClick={() => {
                          const introLang = i18n.language?.startsWith('de') ? 'de' : 'en';
                          const rawIntro = reachOutSuggestions?.intro?.[introLang] || reachOutSuggestions?.intro?.en || '';
                          if (rawIntro) {
                            introTemplateRef.current = rawIntro;
                            isTemplateMessageRef.current = true;
                            const intro = rawIntro.replace(/\{\{job_title\}\}/g, reachOutForm.job_title || '{{job_title}}');
                            setReachOutForm((f) => ({ ...f, message: intro }));
                          }
                        }}
                        className="ml-2 text-[11px] font-medium text-slate-500 hover:text-slate-900"
                      >
                        {t('reach_out.use_suggestion')}
                      </button>
                    )}
                  </label>
                  <textarea
                    value={reachOutForm.message}
                    onChange={(e) => { isTemplateMessageRef.current = false; setReachOutForm((f) => ({ ...f, message: e.target.value })); }}
                    rows={4}
                    className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10"
                    placeholder={t('reach_out.message_placeholder')}
                    maxLength={3000}
                  />
                  <p className={`mt-0.5 text-[11px] font-medium ${reachOutForm.message.length > 2700 ? 'text-red-600' : reachOutForm.message.length > 2400 ? 'text-amber-600' : 'text-slate-400'}`}>
                    {reachOutForm.message.length}/3000
                  </p>
                </div>
                <div>
                  <label className="mb-1.5 block text-xs font-medium text-slate-700">{t('reach_out.nice_to_know')} <span className="font-normal text-slate-400">({t('reach_out.optional')})</span></label>
                  <textarea
                    value={reachOutForm.nice_to_know}
                    onChange={(e) => setReachOutForm((f) => ({ ...f, nice_to_know: e.target.value }))}
                    rows={2}
                    className="w-full resize-none rounded-lg border border-slate-300 px-3 py-2.5 text-sm focus:border-slate-300 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-slate-900/10"
                    placeholder={t('reach_out.nice_to_know_placeholder')}
                    maxLength={500}
                  />
                </div>
              </form>
              <div className="border-t border-slate-100 px-4 py-3 sm:px-5 sm:py-4">
                <div className="flex items-center justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowReachOut(false)}
                    disabled={reachOutSubmitting}
                    className="rounded-lg px-3 py-2 text-sm font-medium text-slate-600 transition-colors hover:bg-slate-50 disabled:opacity-50"
                  >
                    {t('reach_out.cancel')}
                  </button>
                  <button
                    type="button"
                    onClick={submitReachOut}
                    disabled={reachOutSubmitting}
                    className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-slate-800 disabled:opacity-50"
                  >
                    {reachOutSubmitting ? <Loader2 className="size-3.5 animate-spin" /> : <Send className="size-3.5" />}
                    {reachOutSubmitting ? t('reach_out.sending') : t('reach_out.send')}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Profile Nudge Popup */}
      <AnimatePresence>
        {showProfileNudge && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={() => setShowProfileNudge(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-6 h-6 text-slate-600" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('profile_nudge.title')}</h3>
              <p className="text-sm text-slate-500 mb-5">{t('profile_nudge.description')}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowProfileNudge(false)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  {t('profile_nudge.dismiss')}
                </button>
                <button
                  onClick={() => navigate('/recruiter-profile')}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-slate-800 rounded-xl hover:bg-slate-900 transition-colors"
                >
                  {t('profile_nudge.button')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Info dialog */}
      <AnimatePresence>
        {showInfoTip && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
            onClick={() => setShowInfoTip(false)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-t-2xl bg-white p-6 shadow-2xl sm:rounded-2xl"
            >
              <button
                type="button"
                onClick={() => setShowInfoTip(false)}
                className="absolute right-4 top-4 rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="size-5" />
              </button>

              <div className="mb-5 flex items-center gap-3">
                <div className="flex size-10 items-center justify-center rounded-xl bg-pink-50">
                  <Star className="size-5 fill-amber-400 text-amber-400" />
                </div>
                <h2 className="text-lg font-semibold text-slate-900">{t('title')}</h2>
              </div>

              <div className="mb-5 space-y-2">
                <div className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                  <Info className="mt-0.5 size-4 shrink-0 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t('info_sheet.ai_title')}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{t('info_sheet.ai_desc')}</p>
                  </div>
                </div>
                <div className="flex items-start gap-3 rounded-xl border border-slate-100 bg-slate-50/80 p-3">
                  <Send className="mt-0.5 size-4 shrink-0 text-slate-400" />
                  <div>
                    <p className="text-sm font-medium text-slate-800">{t('info_sheet.notify_title')}</p>
                    <p className="mt-0.5 text-xs leading-relaxed text-slate-500">{t('info_sheet.notify_desc')}</p>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => setShowInfoTip(false)}
                className="w-full rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-slate-800"
              >
                {t('info_sheet.got_it')}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Fit Modal */}
      <AnimatePresence>
        {fitSpotlight && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-0 sm:items-center sm:p-4"
            onClick={() => setFitSpotlight(null)}
          >
            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 24 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              onClick={(e) => e.stopPropagation()}
              className="max-h-[85vh] w-full overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:max-w-md sm:rounded-2xl"
            >
              <div className="flex justify-center pt-2 pb-0 sm:hidden">
                <div className="h-1 w-10 rounded-full bg-slate-300" />
              </div>
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-4 sm:px-5">
                <div className="flex items-center gap-2.5">
                  <div className="flex size-8 items-center justify-center rounded-lg bg-slate-900">
                    <TrendingUp className="size-4 text-white" />
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">{t('fit_modal.title')}</h3>
                    {fitSpotlight.target_role && (
                      <p className="text-xs text-slate-500">{fitSpotlight.target_role}</p>
                    )}
                  </div>
                </div>
                <button type="button" onClick={() => setFitSpotlight(null)} className="rounded-lg p-1.5 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600">
                  <X className="size-5" />
                </button>
              </div>
              <div className="px-4 py-4 sm:px-5 sm:pb-5">
                <ul className="space-y-2.5">
                  {(fitSpotlight.fit.includes('\n') ? fitSpotlight.fit.split('\n').filter(Boolean) : [fitSpotlight.fit]).map((b, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                      <span className="mt-2 size-1 shrink-0 rounded-full bg-pink-300" aria-hidden />
                      <span className="leading-relaxed">{b}</span>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  onClick={() => setFitSpotlight(null)}
                  className="mt-5 w-full rounded-xl bg-slate-100 py-2.5 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-200"
                >
                  {t('fit_modal.dismiss')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Dismiss Confirmation */}
      <AnimatePresence>
        {dismissTarget && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
            onClick={() => setDismissTarget(null)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 text-center"
            >
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center mx-auto mb-4">
                <X className="w-6 h-6 text-red-500" />
              </div>
              <h3 className="text-lg font-semibold text-slate-900 mb-2">{t('dismiss_confirm.title')}</h3>
              <p className="text-sm text-slate-500 mb-5">{t('dismiss_confirm.description')}</p>
              <div className="flex gap-3">
                <button
                  onClick={() => setDismissTarget(null)}
                  className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 rounded-xl hover:bg-slate-200 transition-colors"
                >
                  {t('dismiss_confirm.cancel')}
                </button>
                <button
                  onClick={() => {
                    updateStatus(dismissTarget.id, 'dismissed');
                    setDismissTarget(null);
                  }}
                  className="flex-1 px-4 py-2.5 text-sm font-semibold text-white bg-red-500 rounded-xl hover:bg-red-600 transition-colors"
                >
                  {t('dismiss_confirm.confirm')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className={`fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] px-4 py-2.5 rounded-xl shadow-lg text-sm font-medium ${
              toast.type === 'success' ? 'bg-emerald-500 text-white' : toast.type === 'error' ? 'bg-red-500 text-white' : 'bg-slate-800 text-white'
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>
    </RecruiterPageShell>
  );
};

export default Spotlights;
